import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	CogOutline,
	CollaborationMapOutline,
	DocumentReportOutline,
	rem,
	SmileyFaceOutline,
	Stack,
	Text,
	UserGroupOutline,
	VStack,
} from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { Permission, ProtectedContent, useFeatureFlag, usePermission, Variant } from '@bonusly/who-am-i';

import { Conceal } from '../../../../components/display/conceal';
import { Reveal } from '../../../../components/display/reveal';
import { Panel } from '../../../../components/panel';
import { TeamRequestBanner } from '../../../../components/team/team-request-banner';
import { AnalyticsEvent } from '../../../../lib/analytics/events';
import { useAnalytics } from '../../../../lib/analytics/use-analytics';
import { useCurrentCompany } from '../../../../lib/who-am-i/hooks/use-current-company';
import { usePendingConfirmations } from '../../../../queries/team-requests';
import { DirectReport } from '../../../../types/user';
import { Person } from '../../../bonus-feed/features/recognition/components/tracker-widget/types';
import { useAddManagerModal } from '../../hooks/use-add-manager-modal';
import { useHasNoTeamContent } from '../../hooks/use-has-no-team-content';
import { useTeamPanelBadge } from '../../hooks/use-team-panel-badge';
import { IconAndTextContent } from '../icon-and-text-content';
import { UserNavLink } from '../user-nav-link';
import { DirectReportMenu } from './direct-report-menu';
import { TeamTracker } from './team-tracker';

interface TeamPanelProps {
	directReports: DirectReport[];
	isOpen: boolean;
	isSidebarExpanded: boolean;
	onClose: () => void;
	people?: Person[];
}

export const TeamPanel: FC<TeamPanelProps> = ({ directReports, isOpen, isSidebarExpanded, onClose, people }) => {
	const { t } = useTranslation('navigation');
	const { track } = useAnalytics();
	const [isReady, setIsReady] = useState(false);
	const isRemoveHomepageWidgetColumnEnabled = useFeatureFlag(Variant.RemoveHomepageWidgetColumn);
	const canManageTeamRequests = usePermission(Permission.CanManageTeamRequests);
	const hasAnalyticsTier = usePermission(Permission.CanAccessAnalytics);
	const dispatch = useDispatch();
	const { onOpen: onOpenAddManagerModal } = useAddManagerModal();
	const { showBadge, markAsSeen } = useTeamPanelBadge();
	const hasNoTeamContent = useHasNoTeamContent();
	const company = useCurrentCompany();
	const hasOtherMembers = (company?.active_user_count ?? 0) > 1;
	const { data: pendingConfirmationsData } = usePendingConfirmations({ enabled: canManageTeamRequests });
	const pendingRequesters = (pendingConfirmationsData?.records ?? [])
		.filter((r) => r.changes.some((c) => c.changeType === 'change_manager' && c.state === 'pending_confirmation'))
		.map((r) => ({
			id: r.requester?.id ?? r.requesterId,
			name: r.requester?.displayName ?? '',
			profilePic: r.requester?.profilePicUrl ?? '',
		}));

	// Defer rendering children until after the panel has mounted
	// This prevents flicker caused by complex children triggering layout calculations
	useEffect(() => {
		let frameId: number | undefined;

		if (isOpen && !isReady) {
			// Use requestAnimationFrame to wait for the panel to paint first
			frameId = requestAnimationFrame(() => {
				setIsReady(true);
			});
		}
		if (!isOpen) {
			setIsReady(false);
		}

		return () => {
			if (frameId !== undefined) {
				cancelAnimationFrame(frameId);
			}
		};
	}, [isOpen, isReady]);

	return (
		<Panel
			isOpen={isOpen}
			onClose={onClose}
			isSidebarExpanded={isSidebarExpanded}
			title={t('teamMenu.title')}
			closeButtonLabel={t('teamMenu.closePanel')}
			width={rem(363)}
			data-testid="team-panel"
			footer={
				canManageTeamRequests && !hasNoTeamContent ? (
					<VStack spacing={1} w="100%" pt={3} pb={4} px={2} borderTop="1px solid" borderColor="border.soft">
						<UserNavLink
							to="/team/configure"
							onClick={() => track(AnalyticsEvent.TeamPanel.LinkClicked, { link: 'Manage Team' })}
							data-testid="nav-link-my-team-setup"
						>
							<IconAndTextContent icon={<CogOutline size="md" />} label={t('teamMenu.manageTeam')} isExpanded />
						</UserNavLink>
					</VStack>
				) : undefined
			}
		>
			{isReady && (
				<VStack spacing={4} align="stretch">
					{/* TEAM HUB LINKS - MANAGERS ONLY */}
					<ProtectedContent permissions={Permission.CanAccessTeamHub}>
						<VStack id="team-menu-items" spacing={1}>
							{/* YOUR TEAM */}
							<UserNavLink
								to="/team/your-team"
								onClick={() => track(AnalyticsEvent.TeamPanel.LinkClicked, { link: 'Team Hub' })}
								data-testid="nav-link-your-team"
							>
								<IconAndTextContent icon={<UserGroupOutline size="md" />} label={t('teamMenu.teamHub')} isExpanded />
							</UserNavLink>

							{/* PULSE RESULTS */}
							<ProtectedContent permissions={Permission.CanAccessCheckIn}>
								<UserNavLink
									to="/team/pulse-results"
									onClick={() => track(AnalyticsEvent.TeamPanel.LinkClicked, { link: 'Pulse Results' })}
									data-testid="nav-link-pulse-results"
								>
									<IconAndTextContent icon={<SmileyFaceOutline size="md" />} label={t('teamMenu.pulseResults')} isExpanded />
								</UserNavLink>
							</ProtectedContent>

							{/* TEAM REPORT */}
							<UserNavLink
								to="/team/team-report"
								onClick={() => track(AnalyticsEvent.TeamPanel.LinkClicked, { link: 'Team Report' })}
								data-testid="nav-link-team-report"
							>
								<IconAndTextContent icon={<DocumentReportOutline size="md" />} label={t('teamMenu.teamReport')} isExpanded />
							</UserNavLink>

							{/* COLLABORATION MAP */}
							{hasAnalyticsTier && (
								<UserNavLink
									to="/team/collaboration"
									onClick={() => track(AnalyticsEvent.TeamPanel.LinkClicked, { link: 'Collaboration Map' })}
									data-testid="nav-link-collaboration-map"
								>
									<IconAndTextContent icon={<CollaborationMapOutline size="md" />} label={t('teamMenu.collaborationMap')} isExpanded />
								</UserNavLink>
							)}
						</VStack>
					</ProtectedContent>

					{/* ACTIVE TEAM REQUEST */}
					{canManageTeamRequests && pendingRequesters.length > 0 && (
						<Box px={3} py={3} borderTop="1px solid" borderColor="border.soft" mt={2}>
							<TeamRequestBanner variant="compact" requesters={pendingRequesters.slice(0, 3)} onClick={onClose} />
						</Box>
					)}

					{/* TRACKER - PROGRESS BAR + USER LIST */}
					<Reveal if={isRemoveHomepageWidgetColumnEnabled}>
						<TeamTracker directReports={directReports} people={people} />
					</Reveal>

					{canManageTeamRequests && hasNoTeamContent && hasOtherMembers && (
						<Box px={3} py={4} borderTop="1px solid" borderColor="border.soft" mt={4}>
							<Stack gap={3}>
								<Box position="relative">
									<Button
										colorScheme="primary"
										size="md"
										w="100%"
										onClick={() => {
											markAsSeen();
											onClose();
											onOpenAddManagerModal();
										}}
									>
										{t('teamMenu.addYourManager')}
									</Button>
									{showBadge && <Box position="absolute" top={rem(-3)} right={rem(-3)} h={rem(10)} w={rem(10)} bg="error.inputBorder" borderRadius="full" />}
								</Box>
								<Text fontSize="sm" color="text.soft">
									{t('teamMenu.setupPrompt')}
								</Text>
							</Stack>
						</Box>
					)}

					{hasNoTeamContent && !hasOtherMembers && (
						<Box px={3} py={4} borderTop="1px solid" borderColor="border.soft" mt={4}>
							<Stack gap={3}>
								<Button
									colorScheme="primary"
									size="md"
									w="100%"
									onClick={() => {
										markAsSeen();
										onClose();
										dispatch('open-user-invite-modal');
									}}
								>
									{t('teamMenu.inviteYourTeam')}
								</Button>
								<Text fontSize="sm" color="text.soft">
									{t('teamMenu.inviteSetupPrompt')}
								</Text>
							</Stack>
						</Box>
					)}

					<Conceal if={isRemoveHomepageWidgetColumnEnabled}>
						{directReports.length > 0 && (
							<VStack as="nav" aria-label={t('teamMenu.directReportsNavLabel')} spacing={0} w="100%" px={rem(12)}>
								<Text fontSize={100} fontWeight="medium" color="text.softest" textAlign="left" w="100%" mb={1}>
									{t('teamMenu.directReportsNavLabel')}
								</Text>
								{directReports.map((directReport) => (
									<DirectReportMenu key={directReport.id} directReport={directReport} />
								))}
							</VStack>
						)}
					</Conceal>
				</VStack>
			)}
		</Panel>
	);
};
